import { Image as ImageIcon, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '../../ui/catalyst/badge';
import { Button } from '../../ui/catalyst/button';
import { ImagePreviewModal } from './ImagePreviewModal';

export interface AttachmentBadgeProps {
  id: string;
  name: string;
  size: number;
  isUploading?: boolean;
  onRemove: (id: string) => Promise<void>;
  url?: string;
  contentType?: string;
  isAttached?: boolean;
}

export const AttachmentBadge = ({
  id,
  name,
  size,
  isUploading = false,
  onRemove,
  url,
  contentType,
  isAttached = false,
}: AttachmentBadgeProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleRemove = async () => {
    // Prevent actions while uploading or already deleting
    if (isUploading || isDeleting) return;

    try {
      setIsDeleting(true);
      await onRemove(id);
    } catch (error) {
      console.error(`Failed to remove attachment ${name}:`, error);
      setIsDeleting(false); // Reset deleting state on error
    }
  };

  // Check if the attachment is an image
  const isImage =
    contentType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp|svg|avif)$/i.test(name);

  // Create a thumbnail preview for images
  const renderPreview = () => {
    if (!isImage || !url || isUploading) return null;

    return (
      <button
        type="button"
        className="flex items-center justify-center h-5 w-5 mr-1 cursor-pointer bg-transparent border-0 p-0"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setShowImagePreview(true);
        }}
        aria-label={`Preview ${name}`}
      >
        {url ? (
          <img
            src={url}
            alt="" // Empty alt since this is decorative and the button already has an aria-label
            className="h-5 w-5 object-cover rounded"
          />
        ) : (
          <ImageIcon className="h-4 w-4 text-blue-500" />
        )}
      </button>
    );
  };

  return (
    <>
      <Badge
        color="zinc"
        className={`flex items-center gap-1.5 py-1 px-2 ${isDeleting ? 'opacity-60' : ''} ${isImage ? 'hover:bg-gray-100 transition-colors' : ''} ${isUploading || isDeleting ? 'animate-pulse' : ''}`}
        onClick={isImage && url ? () => setShowImagePreview(true) : undefined}
        style={{ cursor: isImage && url ? 'pointer' : 'default' }}
      >
        {renderPreview()}

        <span
          className={`text-xs max-w-36 truncate ${isUploading || isDeleting ? 'text-gray-500' : ''}`}
        >
          {name}
        </span>
        <span className="text-xs text-gray-500">({formatFileSize(size)})</span>

        {!isAttached && (
          <Button
            plain
            className="!p-1"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation(); // Prevent triggering Badge click
              handleRemove();
            }}
            disabled={isUploading || isDeleting}
            aria-label={`Remove ${name}`}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </Badge>

      {/* Image Preview Modal */}
      {showImagePreview && url && (
        <ImagePreviewModal
          imageUrl={url}
          fileName={name}
          onClose={() => setShowImagePreview(false)}
        />
      )}
    </>
  );
};
